import React, { useEffect, useState } from "react";
import { UserCheck, Shield, Eye, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { celebrateNewSigils } from "../lib/sigilCelebration";

const VERIFICATION_BADGES = {
  insider:   { label: "INSIDER",     color: "#ff3b30", desc: "I worked there / I was directly involved" },
  adjacent:  { label: "ADJACENT",    color: "#ffd60a", desc: "I knew someone / I was nearby / I saw it firsthand" },
  secondhand:{ label: "SECONDHAND",  color: "#60a5fa", desc: "I heard it from someone reliable" },
};

/**
 * Sworn Witness Statements block — embedded on dossier / connection / ai-dossier pages.
 * Anonymous-by-default user-generated testimonials with a verification ladder.
 *
 * Props:
 *   targetType: 'dossier' | 'ai_dossier' | 'connection'
 *   targetId:   slug or id
 */
export default function WitnessStatements({ targetType, targetId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [text, setText] = useState("");
  const [verification, setVerification] = useState("secondhand");
  const [displayName, setDisplayName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reload = () => {
    setLoading(true);
    api.get(`/witness/${targetType}/${targetId}`)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [targetType, targetId]);

  const onSubmit = async (e) => {
    e?.preventDefault();
    if (text.trim().length < 20) { toast.error("At least 20 characters"); return; }
    setSubmitting(true);
    try {
      await api.post("/witness", {
        target_type: targetType,
        target_id: targetId,
        text: text.trim(),
        verification_level: verification,
        display_name: displayName.trim() || null,
      });
      setText(""); setDisplayName(""); setVerification("secondhand"); setComposeOpen(false);
      toast.success("Statement filed");
      reload();
      // Filing statements unlocks witness / truth_teller / insider_source sigils
      celebrateNewSigils().catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this statement?")) return;
    try {
      await api.delete(`/witness/${id}`);
      toast.success("Deleted");
      reload();
    } catch { toast.error("Couldn't delete"); }
  };

  return (
    <section className="my-12 border border-white/10 bg-[#0a0a0a] p-6 sm:p-8" data-testid="witness-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono text-[10px] tracking-[0.25em] text-[#ec4899] mb-1 flex items-center gap-2">
            <UserCheck size={11} /> // SWORN WITNESS STATEMENTS · {items.length}
          </div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500">
            // FIRST-PERSON ACCOUNTS. ANONYMOUS BY DEFAULT.
          </div>
        </div>
        <button onClick={() => setComposeOpen(!composeOpen)} data-testid="witness-toggle-compose"
          className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
            composeOpen ? "border border-white/15 text-zinc-400" : "bg-[#ec4899] text-[#050505] hover:bg-white"
          }`}>
          {composeOpen ? "Cancel" : "File a statement"}
        </button>
      </div>

      {composeOpen && (
        <form onSubmit={onSubmit} data-testid="witness-compose-form"
          className="border border-[#ec4899]/40 bg-[#0a0a0a] p-5 mb-6">
          <div className="grid sm:grid-cols-3 gap-3 mb-3">
            {Object.entries(VERIFICATION_BADGES).map(([key, b]) => (
              <button key={key} type="button" onClick={() => setVerification(key)} data-testid={`verification-${key}`}
                title={b.desc}
                className={`p-3 border text-left transition-colors ${
                  verification === key
                    ? "bg-[#0F0F0F] border-white/30"
                    : "border-white/10 hover:border-white/30"
                }`}>
                <div className="font-mono text-[10px] tracking-[0.2em] flex items-center gap-2" style={{ color: b.color }}>
                  {key === "insider" ? <Shield size={11} /> : key === "adjacent" ? <Eye size={11} /> : <UserCheck size={11} />}
                  {b.label}
                </div>
                <div className="font-mono text-[10px] text-zinc-400 mt-2 leading-snug">{b.desc}</div>
              </button>
            ))}
          </div>
          <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={40}
            data-testid="witness-displayname-input"
            placeholder="Pen name (optional — blank = Anonymous)"
            className="w-full bg-[#0a0a0a] border border-white/15 focus:border-[#ec4899] outline-none px-3 py-2 font-mono text-sm text-white transition-colors mb-3" />
          <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} rows={6}
            data-testid="witness-text-input"
            placeholder="What did you see / hear / experience? Concrete details build credibility. Min 20 chars, max 2000."
            className="w-full bg-[#0a0a0a] border border-white/15 focus:border-[#ec4899] outline-none p-3 font-mono text-sm text-white transition-colors" />
          <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
            <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 flex items-center gap-2">
              <AlertTriangle size={11} className="text-[#ff9500]" /> Anonymous by default. Don't dox yourself.
            </div>
            <button disabled={submitting || text.trim().length < 20} type="submit" data-testid="witness-submit-btn"
              className="px-5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] bg-[#ec4899] text-[#050505] hover:bg-white transition-colors disabled:opacity-50">
              {submitting ? "Filing…" : "File Statement"}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="font-mono text-xs text-zinc-500 tracking-[0.2em] py-4">// LOADING STATEMENTS…</div>
      ) : items.length === 0 ? (
        <div className="bg-[#050505] border border-white/10 p-6 text-center font-mono text-xs tracking-[0.2em] text-zinc-500">
          // NO STATEMENTS YET — BE THE FIRST WITNESS
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((s) => {
            const badge = VERIFICATION_BADGES[s.verification_level] || VERIFICATION_BADGES.secondhand;
            const isMine = user && s.user_id === user.id;
            return (
              <li key={s.id} data-testid={`witness-${s.id}`}
                className="border border-white/10 bg-[#0a0a0a] p-4 group">
                <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[10px] tracking-[0.2em] px-2 py-1 border" style={{ color: badge.color, borderColor: `${badge.color}66` }}>
                      {badge.label}
                    </span>
                    <span className="font-mono text-xs text-zinc-300">{s.display_name || "Anonymous"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[10px] tracking-[0.15em] text-zinc-500">
                      {s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}
                    </span>
                    {isMine && (
                      <button onClick={() => onDelete(s.id)} title="Delete" data-testid={`witness-delete-${s.id}`}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-500 hover:text-[#ff3b30]">
                        <X size={12} />
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-zinc-200 text-sm leading-relaxed whitespace-pre-wrap">{s.text}</p>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
